import { Fragment, type ReactNode } from "react";
import type { AiMatch } from "../api/ai.api";
import { parseDoc, parseInline } from "../lib/doc-markdown";
import { SOFT } from "./soft-tokens";

/**
 * One line of a concierge document, as inline nodes.
 *
 * The model marks entities up everywhere — headings, bullets, plain prose —
 * and a renderer that skips this prints the raw `[[slug|Label]]` syntax to the
 * reader. Ported from the phone's render-inline.tsx, which exists as one copy
 * for exactly that reason.
 *
 * Chat kit C4 (2026-10-09): places are bold navy words woven into the
 * sentence, not boxed pills and no longer gold. A place with a catalogue row
 * and a handler is a button that underlines on hover; a name with no row is
 * still bold but plain text, because an unlinked place must never look
 * interactive.
 */
export function renderInline(
  line: string,
  bySlug: Map<string, AiMatch>,
  onEntityPress?: (slug: string) => void,
): ReactNode[] {
  return parseInline(line).map((node, i) => {
    if (node.kind === "bold") {
      return (
        <strong key={i} style={{ fontWeight: 600, color: SOFT.ink }}>
          {node.text}
        </strong>
      );
    }
    if (node.kind === "entity") {
      const match = bySlug.get(node.slug);
      if (!match || !onEntityPress) {
        return (
          <strong key={i} style={{ fontWeight: 600, color: SOFT.ink }}>
            {node.text}
          </strong>
        );
      }
      return (
        <button
          key={i}
          type="button"
          onClick={() => onEntityPress(node.slug)}
          className="font-semibold underline-offset-2 hover:underline"
          style={{ color: SOFT.ink }}
        >
          {node.text}
        </button>
      );
    }
    return <Fragment key={i}>{node.text}</Fragment>;
  });
}

/**
 * An answer that is prose, not a plan. Chat kit C4: no bubble, no card,
 * navy text at reading size with the places in bold.
 *
 * The web used to print the whole answer into one chat bubble, which turned a
 * browse reply — the model writes it as a headed, bulleted document — into a
 * fifteen-line wall of text with the bullets run together. This is the phone's
 * doc-prose: paragraphs as paragraphs, bullets with a gold dot, `##` as a
 * heading, bold as bold, named places as gold words.
 */
export function DocProse({
  text,
  matches,
  onEntityPress,
}: {
  text: string;
  matches: AiMatch[];
  onEntityPress?: (slug: string) => void;
}) {
  const bySlug = new Map(matches.map((m) => [m.slug, m]));
  const blocks = parseDoc(text);

  return (
    <div className="flex flex-col gap-2.5">
      {blocks.map((block, i) =>
        // A stray heading inside a flat answer is rendered rather than dropped:
        // swallowing it would hide a change in the answer's shape.
        block.kind === "heading" ? (
          <h4 key={i} className="mt-2 text-start text-[15px] font-semibold" style={{ color: SOFT.ink }}>
            {renderInline(block.text, bySlug)}
          </h4>
        ) : block.kind === "bullet" ? (
          <div key={i} className="flex items-start gap-2.5">
            {/* Sits on the first line's cap height rather than its box top. */}
            <span
              aria-hidden="true"
              className="mt-[7px] h-[5px] w-[5px] shrink-0 rounded-full"
              style={{ background: SOFT.gold }}
            />
            <p className="min-w-0 flex-1 text-start text-[14px] leading-[22px]" style={{ color: SOFT.ink }}>
              {renderInline(block.text, bySlug, onEntityPress)}
            </p>
          </div>
        ) : (
          <p key={i} className="text-start text-[14px] leading-[22px]" style={{ color: SOFT.ink }}>
            {renderInline(block.text, bySlug, onEntityPress)}
          </p>
        ),
      )}
    </div>
  );
}
